import React, { type ReactNode, useEffect, useMemo, useState } from 'react'

import { useModalContext } from '@fourthwall/components'

import { useCurrentShopQuery } from '../../../../hooks/useCurrentShopQuery'
import type { PaymentMethodItem } from '../../PaymentMethods.types'
import { useShopPaymentMethods } from '../../hooks/useShopPaymentMethods'
import { useSetShopDefaultPaymentMethodMutation } from '../../queries/useSetShopDefaultPaymentMethodMutation'
import { getErrorBanner } from '../../utils/getErrorBanner'
import { AddPaymentMethodModal } from '../AddPaymentMethodModal'
import type { ChildrenProps, SelectPaymentMethodViewType } from './SelectPaymentMethod.types'
import { SelectPaymentMethodView } from './SelectPaymentMethodView'

export type PaymentMethodRequiredModalContainerProps = {
  children: (childrenProps: ChildrenProps) => ReactNode
  submitLabel?: string
  withChargeLimit?: boolean
  onSuccess: () => void
  onAddPaymentMethodClose?: () => void
  onSubmitCallback?: (paymentMethod?: PaymentMethodItem) => void
}

export const SelectPaymentMethodContainer = ({
  submitLabel = 'Confirm',
  withChargeLimit = true,
  children,
  onAddPaymentMethodClose,
  onSuccess,
  onSubmitCallback,
}: PaymentMethodRequiredModalContainerProps) => {
  const { open } = useModalContext()

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<undefined | string>(
    undefined,
  )

  const {
    defaultPaymentMethod,
    shopPaymentMethods,
    isLoadingShopPaymentMethods,
    invalidateShopPaymentMethods,
  } = useShopPaymentMethods()

  const selectPaymentMethodViewType: SelectPaymentMethodViewType = useMemo(() => {
    if (isLoadingShopPaymentMethods) {
      return 'LOADING'
    }
    if (!shopPaymentMethods || shopPaymentMethods.length === 0) {
      return 'NO_PAYMENT_METHODS'
    }

    return 'HAS_PAYMENT_METHODS'
  }, [isLoadingShopPaymentMethods, shopPaymentMethods])

  const { invalidateCurrentShopQuery } = useCurrentShopQuery()
  const { setShopDefaultPaymentMethodMutation } = useSetShopDefaultPaymentMethodMutation()

  const shopPaymentMethodsProps = useMemo(
    () =>
      shopPaymentMethods?.map((paymentMethod) => ({
        ...paymentMethod,
        selected: selectedPaymentMethodId === paymentMethod.id,
        onClick: () => setSelectedPaymentMethodId(paymentMethod.id),
      })),
    [shopPaymentMethods, selectedPaymentMethodId],
  )

  useEffect(() => {
    if (
      defaultPaymentMethod &&
      defaultPaymentMethod.invalid &&
      selectedPaymentMethodId === defaultPaymentMethod.id
    ) {
      const otherPaymentMethod =
        shopPaymentMethods?.find((paymentMethod) => paymentMethod.id !== defaultPaymentMethod.id) ||
        undefined

      setSelectedPaymentMethodId(otherPaymentMethod ? otherPaymentMethod.id : undefined)
    }
  }, [defaultPaymentMethod, selectedPaymentMethodId, shopPaymentMethods])

  useEffect(() => {
    if (defaultPaymentMethod && !defaultPaymentMethod.invalid) {
      setSelectedPaymentMethodId(defaultPaymentMethod.id)
    }
  }, [defaultPaymentMethod])

  const handleSuccess = () => {
    onSuccess()
    invalidateShopPaymentMethods()
    invalidateCurrentShopQuery()
  }

  const handleSubmit = () => {
    if (!selectedPaymentMethodId) {
      return
    }

    const selectedPaymentMethod = shopPaymentMethods?.find(
      (paymentMethod) => paymentMethod.id === selectedPaymentMethodId,
    )

    onSubmitCallback?.(selectedPaymentMethod)

    if (selectedPaymentMethodId === defaultPaymentMethod?.id) {
      return handleSuccess()
    }

    setShopDefaultPaymentMethodMutation.mutate([selectedPaymentMethodId], {
      onSuccess: handleSuccess,
    })
  }

  const handleAddPaymentMethod = () => {
    open(AddPaymentMethodModal, {
      title: 'Add a new payment method',
      withChargeLimit,
      onSuccess: () => {
        onAddPaymentMethodClose?.()
        invalidateShopPaymentMethods()
        invalidateCurrentShopQuery()
      },
      onCancel: () => {
        onAddPaymentMethodClose?.()
      },
    })
  }

  return (
    <SelectPaymentMethodView
      viewType={selectPaymentMethodViewType}
      submitLabel={submitLabel}
      withChargeLimit={withChargeLimit}
      paymentMethodsProps={shopPaymentMethodsProps}
      errorBanner={getErrorBanner(shopPaymentMethodsProps)}
      isSubmitting={setShopDefaultPaymentMethodMutation.isLoading}
      isLoading={isLoadingShopPaymentMethods}
      isDisabled={!selectedPaymentMethodId || selectPaymentMethodViewType === 'LOADING'}
      onAddPaymentMethod={handleAddPaymentMethod}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
    >
      {children}
    </SelectPaymentMethodView>
  )
}
